import { FC, MouseEvent } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { PictureAsPdf as PictureAsPdfIcon } from "@material-ui/icons";
import { find, some } from "lodash";
import { Redirect, useParams } from "react-router";

import { Button } from "components";
import { navigateTo } from "lib/helpers";
import * as routes from "routes";
import { useRoyaltyPayors } from "services/royaltyPayor";
import {
  ADMINMP,
  ASCAP,
  ASCAP_DESIGNATED_USER_INSTRUCTION_URL,
  BMI,
  SESAC,
  SONY,
} from "services/royaltyPayor/constants";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import theme from "theme";

import StepContainer from "../StepContainer";
import ShareYourCredentials from "./components/ShareYourCredentials";
import { Connect, ConnectWithSubaccount } from "./components";
export interface BaseConnectAccountProps {
  royaltyPayor: RoyaltyPayor;
  ConnectAccountInstructions?: FC;
  credentialsCustomPlaceholders?: Record<"username" | "password", string>;
}
const connectRoyaltyPayorAccountComponentDict: Record<
  string,
  FC<BaseConnectAccountProps>
> = {
  [ADMINMP]: ConnectWithSubaccount,
  [ASCAP]: Connect,
  [BMI]: ConnectWithSubaccount,
  [SESAC]: ConnectWithSubaccount,
  [SONY]: ConnectWithSubaccount,
};
const connectRoyaltyPayorAccountInstructionsDict: Record<string, FC> = {
  [ASCAP]: () => {
    const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));

    return (
      <>
        <Typography color="primary" align="left">
          To connect your account, you’ll need to complete a simple process to
          establish a secure connection between ASCAP and Royalty Exchange.
          Follow the instructions below to get started.
        </Typography>
        <Box display="flex" justifyContent="center" my={2}>
          <Button
            customColor="green"
            variant="outlined"
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              navigateTo(ASCAP_DESIGNATED_USER_INSTRUCTION_URL, null, true);
            }}
          >
            <PictureAsPdfIcon />
            &nbsp;&nbsp;Download Instructions
          </Button>
        </Box>
        <Typography color="primary" align={isXsDown ? "left" : "center"}>
          After you complete this process, enter the Designated User credentials
          here to connect your account.
        </Typography>
      </>
    );
  },
};
const connectRoyaltyPayorAccountCustomPlaceholdersDict: Record<
  string,
  Record<"username" | "password", string>
> = {
  [ASCAP]: {
    username: "ASCAP Designated Username",
    password: "ASCAP Designated User Password",
  },
};

const ConnectRoyaltyPayorAccount: FC = () => {
  const { data: royalty_payors, isLoading } = useRoyaltyPayors();
  const { royaltyPayorName } = useParams<{ royaltyPayorName: string }>();

  const selectedRoyaltyPayor = find(royalty_payors, { name: royaltyPayorName });

  const onlyGetCredentials = some(selectedRoyaltyPayor?.permissions, {
    codename: "get_credentials",
  });
  const ConnectRoyaltyPayorAccountComponent = !!selectedRoyaltyPayor
    ? connectRoyaltyPayorAccountComponentDict[selectedRoyaltyPayor.name]
    : undefined;

  return (
    <StepContainer>
      {isLoading ? (
        <CircularProgress />
      ) : !selectedRoyaltyPayor ? (
        <Redirect to={routes.sellerAddRoyaltyAccount()} />
      ) : (
        <>
          {onlyGetCredentials ? (
            <ShareYourCredentials royaltyPayor={selectedRoyaltyPayor} />
          ) : (
            <ConnectRoyaltyPayorAccountComponent
              royaltyPayor={selectedRoyaltyPayor}
              ConnectAccountInstructions={
                connectRoyaltyPayorAccountInstructionsDict[
                  selectedRoyaltyPayor.name
                ]
              }
              credentialsCustomPlaceholders={
                connectRoyaltyPayorAccountCustomPlaceholdersDict[
                  selectedRoyaltyPayor.name
                ]
              }
            />
          )}
        </>
      )}
    </StepContainer>
  );
};

export default ConnectRoyaltyPayorAccount;
